import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Alert,
  FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails, InputAdornment,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import { useSelector } from 'react-redux';
import { userAdminService, AdminUser, ImportRow } from '../../services/userAdminService';
import { tdGroupService } from '../../services/tdGroupService';
import StudentPhotosPanel from './StudentPhotosPanel';

const ACCESS_OPTIONS = ['student', 'teacher', 'admin', 'ci_runner'];

const AdminUsersPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tdGroups, setTdGroups] = useState<string[]>([]);

  // Recherche / filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [onlyNotRegistered, setOnlyNotRegistered] = useState(false);

  // Import
  const [file, setFile] = useState<File | null>(null);
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  // Ajout manuel
  const [newUser, setNewUser] = useState({ login: '', access: '', lastname: '', firstname: '', email: '', td_group: '', is_local: false, password: '' });
  const [creating, setCreating] = useState(false);

  // Correction
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editLogin, setEditLogin] = useState('');
  const [editAccess, setEditAccess] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLastname, setEditLastname] = useState('');
  const [editFirstname, setEditFirstname] = useState('');
  const [editTdGroup, setEditTdGroup] = useState('');
  const [editCanBeSolo, setEditCanBeSolo] = useState(false);
  const [editIsLocal, setEditIsLocal] = useState(false);
  const [editPassword, setEditPassword] = useState('');

  // Suppression
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);

  const loadUsers = React.useCallback(async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      setUsers(await userAdminService.listUsers(token));
    } catch (e: any) {
      setError(e.message || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTdGroups = React.useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      const groups = await tdGroupService.list(token);
      setTdGroups(groups.filter((g) => g.active).map((g) => g.code));
    } catch (e: any) {
      // silencieux : n'empêche pas le reste de la page de fonctionner
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadTdGroups();
    }
  }, [isAdmin, loadUsers, loadTdGroups]);

  // Hooks avant le return anticipé : au F5, userInfos arrive après le 1er rendu (isAdmin
  // false puis true) ; des hooks placés après faisaient planter React (page blanche).
  const groupOptions = useMemo(
    () => Array.from(new Set(users.map((u) => u.td_group).filter((g): g is string => !!g))).sort(),
    [users],
  );

  const normalize = (s: string | null) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  const displayedUsers = useMemo(() => {
    const query = normalize(searchQuery);
    return users
      .filter((u) => !query || normalize(u.lastname).includes(query) || normalize(u.firstname).includes(query))
      .filter((u) => !filterGroup || u.td_group === filterGroup)
      .filter((u) => !onlyNotRegistered || (u.access === 'student' && !u.registration_completed_at))
      .sort((a, b) => {
        if (!!a.is_local !== !!b.is_local) return a.is_local ? 1 : -1;
        return (
          (a.lastname || '').localeCompare(b.lastname || '', 'fr', { sensitivity: 'base', numeric: true }) ||
          (a.firstname || '').localeCompare(b.firstname || '', 'fr', { sensitivity: 'base', numeric: true })
        );
      });
  }, [users, searchQuery, filterGroup, onlyNotRegistered]);

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }

  const runImport = async (apply: boolean) => {
    if (!file) return;
    setImportLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const result = await userAdminService.importStudents(file, apply, token);
      setImportRows(result.rows);
      if (apply) await loadUsers();
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'import");
    } finally {
      setImportLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setCreating(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await userAdminService.createUser(newUser, token);
      setNewUser({ login: '', access: '', lastname: '', firstname: '', email: '', td_group: '', is_local: false, password: '' });
      await loadUsers();
    } catch (e: any) {
      setError(e.message || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (user: AdminUser) => {
    setEditingUser(user);
    setEditLogin(user.login);
    setEditAccess(user.access);
    setEditEmail(user.email || '');
    setEditLastname(user.lastname || '');
    setEditFirstname(user.firstname || '');
    setEditTdGroup(user.td_group || '');
    setEditCanBeSolo(!!user.can_be_solo);
    setEditIsLocal(!!user.is_local);
    setEditPassword('');
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const payload: Parameters<typeof userAdminService.updateUser>[1] = {
        login: editLogin, access: editAccess, email: editEmail || null, lastname: editLastname || null, firstname: editFirstname || null,
        td_group: editTdGroup || null, can_be_solo: editCanBeSolo, is_local: editIsLocal,
      };
      if (editPassword) payload.password = editPassword;
      await userAdminService.updateUser(editingUser.id, payload, token);
      setEditingUser(null);
      await loadUsers();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await userAdminService.deleteUser(confirmDelete.id, token);
      setConfirmDelete(null);
      await loadUsers();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la révocation');
    }
  };

  const importCounts = importRows?.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>Admin Utilisateurs</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table des comptes */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Comptes existants ({displayedUsers.length}/{users.length})</Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            placeholder="Rechercher nom, prénom…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Groupe TD"
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Tous</MenuItem>
            {groupOptions.map((td) => <MenuItem key={td} value={td}>{td}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={onlyNotRegistered} onChange={(e) => setOnlyNotRegistered(e.target.checked)} />}
            label="Non inscrit·e·s uniquement"
          />
        </Box>

        {loadingUsers ? <CircularProgress /> : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px: 1, py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Login</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Prénom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>TD</TableCell>
                  <TableCell>Inscription</TableCell>
                  <TableCell>Monôme autorisé</TableCell>
                  <TableCell>Compte local</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.login}</TableCell>
                    <TableCell>{u.lastname}</TableCell>
                    <TableCell>{u.firstname}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Chip size="small" label={u.access} /></TableCell>
                    <TableCell>{u.td_group}</TableCell>
                    <TableCell>
                      {u.access !== 'student' ? '—' : u.registration_completed_at ? (
                        <Tooltip title={new Date(u.registration_completed_at).toLocaleString('fr-FR')}>
                          <Chip size="small" color="success" label="Inscrit·e" />
                        </Tooltip>
                      ) : (
                        <Chip size="small" color="warning" variant="outlined" label="Non inscrit·e" />
                      )}
                    </TableCell>
                    <TableCell>{u.can_be_solo && <Chip size="small" color="info" label="Oui" />}</TableCell>
                    <TableCell>{u.is_local && <Chip size="small" color="secondary" label="Local" />}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', flexWrap: 'nowrap' }}>
                        <Tooltip title="Corriger">
                          <IconButton size="small" onClick={() => openEdit(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Révoquer l'accès">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(u)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Import listing étudiants */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Importer un listing d'étudiants (xlsx)</Typography>
        </AccordionSummary>
        <AccordionDetails>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
            Choisir un fichier
            <input type="file" hidden accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setImportRows(null); }} />
          </Button>
          {file && <Typography variant="body2">{file.name}</Typography>}
          <Button variant="outlined" disabled={!file || importLoading} onClick={() => runImport(false)}>
            Prévisualiser
          </Button>
          <Button variant="contained" disabled={!file || importLoading || !importRows} onClick={() => runImport(true)}>
            {importLoading ? <CircularProgress size={20} /> : "Confirmer l'import"}
          </Button>
        </Box>

        {importCounts && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {Object.entries(importCounts).map(([status, count]) => (
              <Chip key={status} label={`${status}: ${count}`} color={status === 'created' || status === 'to_create' ? 'success' : status === 'already_exists' ? 'default' : 'warning'} />
            ))}
          </Box>
        )}

        {importRows && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Prénom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>TD</TableCell>
                  <TableCell>Login généré</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importRows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.lastname}</TableCell>
                    <TableCell>{row.firstname}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.td_group}</TableCell>
                    <TableCell>{row.login}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        </AccordionDetails>
      </Accordion>

      <StudentPhotosPanel />

      {/* Ajout manuel */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Ajouter un utilisateur manuellement</Typography>
        </AccordionSummary>
        <AccordionDetails>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField label="Login" size="small" value={newUser.login} onChange={(e) => setNewUser({ ...newUser, login: e.target.value })} />
          <TextField
            select
            label="Rôle"
            size="small"
            value={newUser.access}
            onChange={(e) => setNewUser({ ...newUser, access: e.target.value })}
            sx={{ minWidth: 120 }}
          >
            {ACCESS_OPTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField label="Nom" size="small" value={newUser.lastname} onChange={(e) => setNewUser({ ...newUser, lastname: e.target.value })} />
          <TextField label="Prénom" size="small" value={newUser.firstname} onChange={(e) => setNewUser({ ...newUser, firstname: e.target.value })} />
          <TextField label="Email" size="small" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
          <TextField
            select
            label="TD (optionnel)"
            size="small"
            value={newUser.td_group}
            onChange={(e) => setNewUser({ ...newUser, td_group: e.target.value })}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">—</MenuItem>
            {tdGroups.map(td => <MenuItem key={td} value={td}>{td}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={newUser.is_local} onChange={(e) => setNewUser({ ...newUser, is_local: e.target.checked })} />}
            label="Compte local"
          />
          {newUser.is_local && (
            <TextField
              label="Mot de passe"
              type="password"
              size="small"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            />
          )}
          <Button variant="contained" startIcon={<PersonAddIcon />} disabled={!newUser.login || !newUser.access || (newUser.is_local && !newUser.password) || creating} onClick={handleCreateUser}>
            Ajouter
          </Button>
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* Dialogue de correction */}
      <Dialog open={!!editingUser} onClose={() => setEditingUser(null)}>
        <DialogTitle>Corriger {editingUser?.login}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Login" value={editLogin} onChange={(e) => setEditLogin(e.target.value)} sx={{ mt: 1 }} />
          <TextField label="Nom" value={editLastname} onChange={(e) => setEditLastname(e.target.value)} />
          <TextField label="Prénom" value={editFirstname} onChange={(e) => setEditFirstname(e.target.value)} />
          <TextField label="Email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
          <TextField select label="Rôle" value={editAccess} onChange={(e) => setEditAccess(e.target.value)}>
            {ACCESS_OPTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField select label="TD" value={editTdGroup} onChange={(e) => setEditTdGroup(e.target.value)}>
            <MenuItem value="">—</MenuItem>
            {tdGroups.map(td => <MenuItem key={td} value={td}>{td}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={editCanBeSolo} onChange={(e) => setEditCanBeSolo(e.target.checked)} />}
            label="Monôme autorisé (le groupe est complet dès son premier choix)"
          />
          <FormControlLabel
            control={<Switch checked={editIsLocal} onChange={(e) => setEditIsLocal(e.target.checked)} />}
            label="Compte local (mot de passe, pas de connexion CAS)"
          />
          {editIsLocal && (
            <TextField
              label="Nouveau mot de passe"
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              helperText={editingUser?.is_local ? "Laisser vide pour conserver le mot de passe actuel" : "Requis pour activer un compte local"}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingUser(null)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={editIsLocal && !editingUser?.is_local && !editPassword}
            onClick={handleSaveEdit}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation de suppression */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Révoquer l'accès de {confirmDelete?.login} ?</DialogTitle>
        <DialogContent>
          <DialogContentText>Cette action supprime le compte. Irréversible.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Confirmer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsersPage;
